import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { ExclamationTriangleIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { LineCloseModalProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

/**
 * LineCloseModal — confirma el cierre o reapertura de una línea de documento.
 *
 * En modo 'close' solicita una razón de cierre (textarea obligatorio).
 * En modo 'reopen' muestra solo un mensaje de confirmación.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - LineCloseModalProps
 *
 * @example
 * ```tsx
 * <LineCloseModal
 *   open={isOpen}
 *   mode="close"
 *   lineDescription="Producto A — 100 unidades"
 *   onClose={() => setOpen(false)}
 *   onConfirm={(reason) => closeLine(lineId, reason)}
 * />
 * ```
 */
export function LineCloseModal({
  open,
  onClose,
  onConfirm,
  mode,
  lineDescription,
  isLoading = false,
  error,
  className,
}: LineCloseModalProps) {
  const { t } = useMasterDocumentTranslation();
  const [closeReason, setCloseReason] = useState('');

  useEffect(() => {
    if (!open) {
      setCloseReason('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const isCloseMode = mode === 'close';
  const confirmDisabled = isLoading || (isCloseMode && closeReason.trim() === '');

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[440px]',
          'bg-bg-primary rounded-2xl px-6 py-5',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          className,
        )}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="line-close-modal-title"
        aria-describedby={isCloseMode ? 'line-close-modal-desc' : 'line-close-modal-reopen-desc'}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
          aria-label={t('master_document.chrome.close')}
        >
          <XMarkIcon className="w-4 h-4" aria-hidden="true" />
        </button>

        {/* Icon */}
        <div
          className={clsx(
            'flex items-center justify-center w-12 h-12 rounded-full mb-4',
            isCloseMode
              ? 'bg-amber-50 dark:bg-amber-700/15'
              : 'bg-primary-custom-100 dark:bg-primary-custom-900/20',
          )}
        >
          {isCloseMode ? (
            <ExclamationTriangleIcon
              className="w-6 h-6 text-amber-700 dark:text-amber-400"
              aria-hidden="true"
            />
          ) : (
            <ArrowPathIcon
              className="w-6 h-6 text-primary-custom-600 dark:text-primary-custom-400"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Title */}
        <h2
          id="line-close-modal-title"
          className="text-base font-bold text-content-primary dark:text-dark-content-primary mb-1"
        >
          {isCloseMode ? t('master_document.lines.closeLine') : t('master_document.lines.reopenLine')}
        </h2>

        {/* Line description */}
        {lineDescription && (
          <p
            id="line-close-modal-desc"
            className="text-sm text-content-tertiary dark:text-dark-content-secondary mb-4"
          >
            {lineDescription}
          </p>
        )}

        {/* Body */}
        {isCloseMode ? (
          <div className="mb-4">
            <label
              htmlFor="close-reason"
              className="block text-sm font-semibold text-content-primary dark:text-dark-content-primary mb-1.5"
            >
              {t('master_document.lines.closeReason')}{' '}
              <span className="text-red-600 dark:text-red-400" aria-hidden="true">
                *
              </span>
            </label>
            <textarea
              id="close-reason"
              rows={3}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder={t('master_document.lines.closeReasonPlaceholder')}
              className={clsx(
                'w-full resize-none rounded-lg px-3 py-2 text-sm',
                'border border-border-primary bg-bg-primary text-content-primary',
                'placeholder:text-content-tertiary',
                'transition-colors duration-150',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
                'dark:placeholder:text-dark-content-secondary',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              )}
            />
          </div>
        ) : (
          <p
            id="line-close-modal-reopen-desc"
            className="text-sm text-content-tertiary dark:text-dark-content-secondary mb-4"
          >
            {t('master_document.lines.reopenConfirm')}
          </p>
        )}

        {/* Error banner */}
        {error && (
          <div
            className={clsx(
              'bg-red-50 border border-red-200 rounded-lg px-3 py-2',
              'text-sm text-red-700 mb-3',
              'dark:bg-red-950/20 dark:border-red-800 dark:text-red-300',
            )}
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary text-content-tertiary hover:bg-background-secondary transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              isLoading && 'opacity-50 cursor-not-allowed',
            )}
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(isCloseMode ? closeReason : undefined)}
            disabled={confirmDisabled}
            className={clsx(
              'flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              isCloseMode
                ? 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
                : 'bg-primary-inverse-background text-white hover:bg-primary-custom-700 dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              confirmDisabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {isLoading && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
                className="w-4 h-4 animate-spin"
              >
                <path
                  fillRule="evenodd"
                  d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {isCloseMode ? t('master_document.lines.closeLine') : t('master_document.lines.reopenLine')}
          </button>
        </div>
      </div>
    </>
  );
}
